/**
 * 单告警码证据链诊断 API 模块
 * 对应智能体接口：POST /alarm-analysis/evidence-diagnosis (SSE 流式输出)
 */

export const EVIDENCE_DIAGNOSIS_URL = '/new-agent/alarm-analysis/evidence-diagnosis'

/**
 * 将告警列表行数据严格映射为智能体入参白名单对象
 * 遵循协议约束：
 * 1. stationId 必须为字符串类型（传数字 TDengine 会静默返回 0 行，导致 422 拒绝）；
 * 2. extra="forbid"：严禁包含任何协议外字段；
 * 3. 必须包含 alarmCode, alarmTime, equipmentUuid, stationId。
 *
 * @param {Record<string, any>} row 列表行原始数据
 * @returns {Record<string, any>} 智能体入参 Payload
 */
export function buildEvidenceDiagnosisPayload(row) {
  if (!row) {
    throw new Error('未提供告警记录数据')
  }

  // 1. 取证核心入参提取
  const alarmCode = String(row.alarmCode || row.code || '').trim()
  if (!alarmCode || alarmCode === '--') {
    throw new Error('告警码 (alarmCode) 缺失')
  }

  // 告警时间优先取 lastTime，次取 startTime
  const rawTime = row.lastTime && row.lastTime !== '--'
    ? row.lastTime
    : (row.startTime && row.startTime !== '--' ? row.startTime : '')
  const alarmTime = String(rawTime || '').trim()
  if (!alarmTime) {
    throw new Error('告警时刻 (alarmTime) 缺失')
  }

  // 设备坐标 UUID
  const equipmentUuid = String(row.uuid && row.uuid !== '--' ? row.uuid : (row.equipmentUuid || row.deviceVid || '')).trim()
  if (!equipmentUuid) {
    throw new Error('设备坐标 (equipmentUuid) 缺失，无法定位证据源')
  }

  // 电站 ID 强制为字符串
  const stationId = String(row.stationId != null && row.stationId !== '--' ? row.stationId : '').trim()
  if (!stationId) {
    throw new Error('电站编号 (stationId) 缺失')
  }

  // 2. 组装取证必填字段
  const payload = {
    alarmCode,
    alarmTime,
    equipmentUuid,
    stationId,
    locale: 'zh-CN',
  }

  // 3. 严格白名单展示回显字段（不参与取证，仅回显）
  const alarmId = row.id || row.alarmId || row.key
  if (alarmId && String(alarmId) !== '--') {
    payload.alarmId = String(alarmId)
  }

  const alarmName = row.description || row.alarmName || row.equipmentName
  if (alarmName && String(alarmName) !== '--') {
    payload.alarmName = String(alarmName)
  }

  if (row.level != null && !Number.isNaN(Number(row.level))) {
    payload.severityLevel = Number(row.level)
  }

  if (row.severityLabel) {
    payload.severityLabel = String(row.severityLabel)
  }

  if (row.triggerCount != null && !Number.isNaN(Number(row.triggerCount)) && Number(row.triggerCount) >= 0) {
    payload.alarmCount = Number(row.triggerCount)
  }

  if (row.startTime && row.startTime !== '--') {
    payload.firstAlarmTime = String(row.startTime)
  }

  if (row.lastTime && row.lastTime !== '--') {
    payload.lastAlarmTime = String(row.lastTime)
  }

  if (row.endTime) {
    payload.eventStatus = row.endTime === '--' || !row.endTime ? 'active' : 'recovered'
  }

  if (row.devId && String(row.devId) !== '--') {
    payload.devId = String(row.devId)
  }

  return payload
}

/**
 * 发起单告警码证据链诊断流式请求 (POST SSE)
 *
 * @param {Record<string, any>} payload 入参包（由 buildEvidenceDiagnosisPayload 构建）
 * @param {object} handlers 回调函数集合
 * @param {Function} [handlers.onStarted] 诊断启动 (analysis_started)
 * @param {Function} [handlers.onStepDetail] 步骤进展 (step_detail)
 * @param {Function} [handlers.onEvidenceReady] 确定性事实与逐条证据就绪 (evidence_ready)
 * @param {Function} [handlers.onCompleted] 全量诊断结论就绪 (analysis_completed)
 * @param {Function} [handlers.onFailed] 取证或模型失败 (analysis_failed)
 * @param {Function} [handlers.onDone] 诊断结束 (done)
 * @param {AbortSignal} [signal] 用于中断请求的 AbortSignal
 * @returns {Promise<void>}
 */
export async function streamEvidenceDiagnosis(payload, handlers = {}, signal = null) {
  const response = await fetch(EVIDENCE_DIAGNOSIS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  })

  // 422 业务参数校验错误为普通 JSON，非 SSE
  if (!response.ok) {
    let errorText = ''
    try {
      const errJson = await response.json()
      errorText = errJson.detail || errJson.message || JSON.stringify(errJson)
    } catch {
      errorText = await response.text()
    }
    throw new Error(`诊断请求失败 (HTTP ${response.status}): ${errorText}`)
  }

  if (!response.body) {
    throw new Error('服务端未返回可读数据流')
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += value

      // SSE 事件以连续换行分隔（同时兼容 \r\n\r\n 和 \n\n）
      const chunks = buffer.split(/(?:\r?\n){2}/)
      buffer = chunks.pop() || ''

      for (const chunk of chunks) {
        if (!chunk.trim()) continue

        // 提取以 data: 开头的有效载荷行
        const lines = chunk.split('\n')
        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine.startsWith('data:')) continue

          const jsonStr = trimmedLine.slice(5).trim()
          if (!jsonStr) continue

          try {
            const event = JSON.parse(jsonStr)
            dispatchDiagnosisEvent(event, handlers)
          } catch (e) {
            console.warn('[EvidenceDiagnosis] 解析 SSE 事件失败:', e, jsonStr)
          }
        }
      }
    }

    // 处理残余 buffer（如果有完整未结尾的一条）
    if (buffer.trim() && buffer.includes('data:')) {
      const lines = buffer.split('\n')
      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed.startsWith('data:')) {
          try {
            const event = JSON.parse(trimmed.slice(5).trim())
            dispatchDiagnosisEvent(event, handlers)
          } catch (e) {
            console.warn('[EvidenceDiagnosis] 解析尾部 SSE 事件失败:', e)
          }
        }
      }
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.info('[EvidenceDiagnosis] 请求已被用户手动中止')
      return
    }
    throw error
  } finally {
    reader.releaseLock()
  }
}

/**
 * 分发事件至对应 handler
 */
function dispatchDiagnosisEvent(event, handlers) {
  if (!event || !event.type) return

  switch (event.type) {
    case 'analysis_started':
      handlers.onStarted?.(event)
      break
    case 'step_detail':
      handlers.onStepDetail?.(event)
      break
    case 'evidence_ready':
      handlers.onEvidenceReady?.(event)
      break
    case 'analysis_completed':
      handlers.onCompleted?.(event)
      break
    case 'analysis_failed':
      handlers.onFailed?.(event)
      break
    case 'done':
      handlers.onDone?.(event)
      break
    default:
      handlers.onMessage?.(event)
      break
  }
}
